import { useState, useEffect, useMemo } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import moment from "moment";
import "moment/locale/es";

moment.locale("es");

const customStyles = {
  content: {
    width: "500px",
    height: "620px",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "24px 32px",
    borderRadius: "16px",
    border: "none",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    fontFamily: "Inter, sans-serif",
    "::WebkitScrollbar": {
      width: "0px",
      background: "transparent",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,0.3)",
    zIndex: 1008,
  },
};

export default function ModalCreateEvent({
  isOpen,
  onClose,
  onSave,
  isNew,
  initialData,
  categories = [],
}) {
  /* ---------- STATE ---------- */
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [note, setNote] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Eventos");
  const [color, setColor] = useState("");
  const [hoverCancel, setHoverCancel] = useState(false);
  const [hoverSave, setHoverSave] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  /* ---------- PALETA DE COLORES ---------- */
  const taskColorOptions = [
    { hex: "#e0e7ff" },
    { hex: "#ede9fe" },
    { hex: "#bae6fd" },
    { hex: "#fecaca" },
    { hex: "#fef9c3" },
    { hex: "#f0f0f0" },
    { hex: "#dddddd" },
  ];

  const categoryOptions = useMemo(() => {
    const blacklist = ["eventos", "festivos", "cotizaciones"];
    const uniques = categories
      .filter((c) => !blacklist.includes(c.label.trim().toLowerCase()))
      .reduce((acc, cur) => {
        const l = cur.label.trim().toLowerCase();
        if (!acc.some((a) => a.label.trim().toLowerCase() === l)) acc.push(cur);
        return acc;
      }, []);
    return [{ label: "Eventos", color: "#007bff" }, ...uniques];
  }, [categories]);

  useEffect(() => {
    if (!catOpen) return;
    const close = (e) =>
      !e.target.closest?.("#cat-select") && setCatOpen(false);
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [catOpen]);

  useEffect(() => {
    if (initialData) {
      // <<< esto se ejecuta tanto para editar como para NUEVO
      setTitle(initialData.title || "");
      setNote(initialData.note || "");

      const s = moment(initialData.start);
      setStartDate(s.format("YYYY-MM-DD"));
      setStartTime(s.format("HH:mm"));

      const e = moment(initialData.end);
      setEndDate(e.format("YYYY-MM-DD"));
      setEndTime(e.format("HH:mm"));

      setSelectedCategory(
        initialData.resource === "evento" ? "Eventos" : initialData.category
      );
      setColor(initialData.color);
    } else {
      // “+ Nuevo” desde el botón principal
      const now = moment();
      setTitle("");
      setNote("");
      setStartDate(now.format("YYYY-MM-DD"));
      setStartTime(now.format("HH:mm"));
      setEndDate("");
      setEndTime("");
      setSelectedCategory("Eventos");
      setColor("");
    }
  }, [initialData]);

  /* ---------- EVITAR SALTO DEL BODY ---------- */
  useEffect(() => {
    if (isOpen) {
      const sbWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = sbWidth ? `${sbWidth}px` : "0";
    } else {
      document.body.style.paddingRight = "0";
    }
    return () => (document.body.style.paddingRight = "0");
  }, [isOpen]);

  /* ---------- GUARDAR ---------- */
  const handleSubmit = async () => {
    if (
      !title.trim() ||
      !startDate ||
      !startTime ||
      !endDate ||
      !endTime ||
      !color
    ) {
      Swal.fire(
        "Error",
        "Título, fecha y hora de inicio/fin son obligatorios.",
        "error"
      );
      return;
    }

    const startISO = `${startDate}T${startTime}`;
    const endISO = `${endDate}T${endTime}`;

    if (new Date(endISO) <= new Date(startISO)) {
      Swal.fire(
        "Error",
        "La fecha/hora de término debe ser posterior al inicio.",
        "error"
      );
      return;
    }

    await onSave({
      id: initialData?.id || null,
      title,
      note,
      start: startISO,
      end: endISO,
      category: selectedCategory,
      color,
      resource: selectedCategory === "Eventos" ? "evento" : "tarea",
    });
    onClose();
  };

  /* ============ RENDER ============ */
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel={initialData ? "Editar Evento" : "Agregar Evento"}
    >
      {/* BOTÓN CERRAR */}
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: "16px",
          right: "16px",
          backgroundColor: "#F3F4F6",
          border: "none",
          borderRadius: "50%",
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <i
          className="bi bi-x-lg"
          style={{ fontSize: "1rem", color: "#374151" }}
        />
      </button>

      <h2 style={{ marginBottom: "24px", fontSize: "1.5rem", fontWeight: 600 }}>
        {isNew ? "Agregar Evento" : "Editar Evento"}
      </h2>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* TÍTULO */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{ display: "block", marginBottom: "6px", fontWeight: 500 }}
          >
            Título
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Agrega un nuevo evento"
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              boxSizing: "border-box",
            }}
            required
          />
        </div>

        {/* NOTA */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{ display: "block", marginBottom: "6px", fontWeight: 500 }}
          >
            Nota
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Agrega una descripción"
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              boxSizing: "border-box",
              fontFamily: '"Inter", sans-serif',
            }}
          />
        </div>

        {/* CATEGORÍA */}
        <div
          style={{ marginBottom: "24px", position: "relative" }}
          id="cat-select"
        >
          <label
            style={{ display: "block", marginBottom: "8px", fontWeight: 500 }}
          >
            Categoría
          </label>

          {/* “Input” visual */}
          <div
            onClick={() => setCatOpen((o) => !o)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              padding: "10px 12px",
              border: "1.5px solid #d1d5db",
              borderRadius: "8px",
              cursor: "pointer",
              userSelect: "none",
              boxSizing: "border-box",
            }}
          >
            {/* punto de color */}
            <span
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor:
                  categoryOptions.find((c) => c.label === selectedCategory)
                    ?.color || "#ddd",
                flexShrink: 0,
              }}
            />
            <span style={{ flex: 1 }}>{selectedCategory}</span>
            <i className={`bi bi-chevron-${catOpen ? "up" : "down"}`} />
          </div>

          {/* Lista desplegable */}
          {catOpen && (
            <ul
              style={{
                marginTop: "4px",
                width: "100%",
                maxHeight: "200px",
                overflowY: "auto",
                background: "#fff",
                border: "1.5px solid #d1d5db",
                borderRadius: "8px",
                boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                listStyle: "none",
                padding: "4px 0",
              }}
            >
              {categoryOptions.map((opt) => (
                <li
                  key={opt.label}
                  onClick={() => {
                    setSelectedCategory(opt.label);
                    setCatOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    background:
                      selectedCategory === opt.label
                        ? "rgba(59,130,246,0.1)"
                        : "transparent",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor: opt.color || "#ddd",
                      flexShrink: 0,
                    }}
                  />
                  <span>{opt.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* PALETA DE COLORES – siempre visible */}
        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", marginBottom: "6px", fontWeight: 500 }}
          >
            Color
          </label>

          {/* radiogroup nativo */}
          <div style={{ display: "flex", gap: "8px" }} role="radiogroup">
            {taskColorOptions.map((opt, idx) => (
              <label
                key={idx}
                style={{ position: "relative", cursor: "pointer" }}
              >
                {/* ---- radio oculto pero válido ---- */}
                <input
                  type="radio"
                  name="eventColor"
                  value={opt.hex}
                  required={idx === 0}
                  checked={color === opt.hex}
                  onChange={() => setColor(opt.hex)}
                  style={{
                    position: "absolute",
                    opacity: 0 /* visualmente invisible */,
                    pointerEvents: "none",
                  }}
                />

                {/* ---- círculo visible ---- */}
                <span
                  onClick={() =>
                    setColor(opt.hex)
                  } /* permite click en el span */
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    backgroundColor: opt.hex,
                    display: "block",
                    border:
                      color === opt.hex
                        ? "2px solid #333"
                        : "2px solid transparent",
                  }}
                />
              </label>
            ))}
          </div>
        </div>

        {/* FECHA Y HORA */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          {/* Fecha / Hora inicio y fin */}
          <div>
            <label
              style={{ display: "block", marginBottom: "4px", fontWeight: 500 }}
            >
              Fecha Inicial
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                boxSizing: "border-box",
              }}
              required
            />
          </div>
          <div>
            <label
              style={{ display: "block", marginBottom: "4px", fontWeight: 500 }}
            >
              Hora Inicial
            </label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                boxSizing: "border-box",
              }}
              required
            />
          </div>
          <div>
            <label
              style={{ display: "block", marginBottom: "4px", fontWeight: 500 }}
            >
              Fecha Final
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                boxSizing: "border-box",
              }}
              required
            />
          </div>
          <div>
            <label
              style={{ display: "block", marginBottom: "4px", fontWeight: 500 }}
            >
              Hora Final
            </label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                boxSizing: "border-box",
              }}
              required
            />
          </div>
        </div>

        {/* BOTONES */}
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
        >
          <button
            type="button"
            onClick={onClose}
            onMouseEnter={() => setHoverCancel(true)}
            onMouseLeave={() => setHoverCancel(false)}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "none",
              backgroundColor: hoverCancel ? "#d1d5db" : "#e5e7eb",
              color: "#374151",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
            }}
          >
            Cancelar
          </button>
          <button
            type="submit"
            onMouseEnter={() => setHoverSave(true)}
            onMouseLeave={() => setHoverSave(false)}
            style={{
              padding: "8px 20px",
              borderRadius: "6px",
              border: "none",
              backgroundColor: hoverSave ? "#2563eb" : "#3b82f6",
              color: "#fff",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
            }}
          >
            {initialData ? "Guardar Cambios" : "Agregar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
